import React, { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Check, X, Star, Trash2, LogOut, Eye, Heart, Plus, Pencil, KeyRound, LayoutDashboard, FolderKanban, Newspaper, SlidersHorizontal, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import AdminProjectModal from "../components/AdminProjectModal";

const STATUS_STYLE = {
  pending: "border-amber-300 bg-amber-50 text-amber-600",
  approved: "border-emerald-300 bg-emerald-50 text-emerald-600",
  rejected: "border-rose-300 bg-rose-50 text-rose-500",
};

const inputCls =
  "w-full rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 placeholder-slate-400 outline-none transition-colors focus:border-[#2563EB] focus:bg-white";
const labelCls = "mb-2 block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400";

function LoginForm() {
  const { login } = useAuth();
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      toast.success(t.login_welcome);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center px-5 pt-24" data-testid="admin-login-screen">
      <motion.form
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={submit}
        className="w-full max-w-md rounded-xl border border-slate-200/90 bg-white p-9 shadow-[0_12px_40px_-8px_rgba(15,23,42,0.1)]"
      >
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.login_tag}</p>
        <h1 className="mt-2 font-display text-2xl font-bold text-slate-900">{t.login_title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.login_sub}</p>
        <div className="mt-7 space-y-4">
          <input type="text" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email-input" placeholder={t.login_id_ph} className={inputCls} />
          <input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password-input" placeholder={t.login_pw_ph} className={inputCls} />
          {error && <p className="text-sm text-rose-500" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full rounded-full bg-[#1E3A8A] py-3.5 font-mono text-xs uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#2563EB] disabled:opacity-50">
            {busy ? t.login_busy : t.login_btn}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

function NewsManager({ t }) {
  const EMPTY = { title: "", excerpt: "", tag: "News", date: "", image_url: "" };
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => api.get("/news").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return;
    try {
      if (editing) {
        await api.put(`/admin/news/${editing}`, form);
        toast.success("News item updated");
      } else {
        await api.post("/admin/news", form);
        toast.success("News item published");
      }
      setForm(EMPTY); setEditing(null); setShowForm(false); load();
    } catch { toast.error("Save failed"); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/news/${id}`); toast.success("News item deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="news-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-lg font-semibold text-slate-900">{t.news_mgr}</h3>
        <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm(EMPTY); }} data-testid="news-add-button" className="flex items-center gap-1.5 rounded-full bg-[#1E3A8A] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white transition-colors hover:bg-[#2563EB]">
          <Plus size={12} /> {t.news_new}
        </button>
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-5 space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-5" data-testid="news-form">
          <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#2563EB]">{editing ? t.news_edit : t.news_new}</p>
          <input className={inputCls} placeholder={t.news_f_title} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="news-title-input" />
          <textarea className={inputCls} rows={2} placeholder={t.news_f_excerpt} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="news-excerpt-input" />
          <div className="grid gap-4 sm:grid-cols-3">
            <input className={inputCls} placeholder={t.news_f_tag} value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} data-testid="news-tag-input" />
            <input className={inputCls} type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="news-date-input" />
            <input className={inputCls} placeholder={t.news_f_img} value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="news-image-input" />
          </div>
          <div className="flex gap-2.5">
            <button onClick={save} data-testid="news-save-button" className="rounded-full bg-emerald-600 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-white hover:bg-emerald-500">
              {editing ? t.news_update : t.news_publish}
            </button>
            <button onClick={() => { setShowForm(false); setEditing(null); setForm(EMPTY); }} data-testid="news-cancel-button" className="rounded-full border border-slate-200 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:text-slate-800">
              {t.news_cancel}
            </button>
          </div>
        </motion.div>
      )}

      <div className="mt-5 divide-y divide-slate-100" data-testid="news-list">
        {items.length === 0 && <p className="py-6 text-sm text-slate-400">{t.news_empty}</p>}
        {items.map((n) => (
          <div key={n.id} className="flex flex-wrap items-center gap-3 py-3.5" data-testid={`news-row-${n.id}`}>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">{n.tag}</span>
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{n.title}</span>
            <span className="font-mono text-[10px] text-slate-400">{n.date}</span>
            <button onClick={() => { setForm({ title: n.title, excerpt: n.excerpt, tag: n.tag, date: n.date, image_url: n.image_url }); setEditing(n.id); setShowForm(true); }} data-testid={`news-edit-${n.id}`} className="flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]">
              <Pencil size={11} /> {t.news_edit_btn}
            </button>
            <button onClick={() => remove(n.id)} data-testid={`news-delete-${n.id}`} className="flex items-center gap-1 rounded-md border border-slate-200 px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.12em] text-slate-500 hover:border-rose-300 hover:text-rose-500">
              <Trash2 size={11} /> {t.news_delete}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FeatureToggles({ t }) {
  const [settings, setSettings] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const toggle = async (key) => {
    const next = { ...settings, [key]: !settings[key] };
    setSettings(next);
    try {
      const r = await api.put("/admin/settings", { [key]: next[key] });
      setSettings(r.data);
      toast.success("Site feature updated");
    } catch {
      toast.error("Update failed");
    }
  };

  if (!settings) return null;
  const TOGGLES = [
    ["news", t.toggle_news], ["marquee", t.toggle_marquee], ["manifesto", t.toggle_manifesto],
    ["departments", t.toggle_departments], ["submissions", t.toggle_submissions],
  ];

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="feature-toggles">
      <h3 className="font-display text-lg font-semibold text-slate-900">{t.feat_toggles}</h3>
      <p className="mt-1 text-sm text-slate-500">{t.feat_toggles_sub}</p>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        {TOGGLES.map(([key, label]) => (
          <button key={key} onClick={() => toggle(key)} data-testid={`toggle-${key}`} className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-4 py-3.5 text-left transition-colors hover:border-[#2563EB]/40">
            <span className="text-sm font-medium text-slate-700">{label}</span>
            <span className={`relative h-6 w-11 rounded-full transition-colors duration-300 ${settings[key] ? "bg-[#2563EB]" : "bg-slate-300"}`}>
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-300 ${settings[key] ? "left-[22px]" : "left-0.5"}`} />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function CredentialsPanel({ t }) {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_email: "", new_password: "" });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { current_password: form.current_password };
      if (form.new_email.trim()) payload.new_email = form.new_email.trim();
      if (form.new_password) payload.new_password = form.new_password;
      await api.post("/admin/credentials", payload);
      toast.success("Credentials updated. Signing you out — sign in with the new credentials.");
      setTimeout(logout, 1800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="credentials-panel">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><KeyRound size={17} className="text-[#2563EB]" /> {t.creds_title}</h3>
      <p className="mt-1 text-sm text-slate-500">{t.creds_sub}</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <div>
          <label className={labelCls}>{t.creds_current} *</label>
          <input type="password" autoComplete="current-password" className={inputCls} value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="creds-current-password" />
        </div>
        <div>
          <label className={labelCls}>{t.creds_new_id}</label>
          <input type="text" autoComplete="off" className={inputCls} placeholder={user?.email} value={form.new_email} onChange={(e) => setForm({ ...form, new_email: e.target.value })} data-testid="creds-new-id" />
        </div>
        <div>
          <label className={labelCls}>{t.creds_new_pw}</label>
          <input type="password" autoComplete="new-password" className={inputCls} value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="creds-new-password" />
        </div>
      </div>
      <button onClick={save} disabled={busy || !form.current_password || (!form.new_email.trim() && !form.new_password)} data-testid="creds-save-button" className="mt-5 rounded-full bg-slate-900 px-6 py-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#1E3A8A] disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? t.creds_saving : t.creds_save}
      </button>
    </div>
  );
}

function TrashPanel({ t, onChanged }) {
  const [items, setItems] = useState([]);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(() => api.get("/admin/trash").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const restore = async (id) => {
    try { await api.post(`/admin/projects/${id}/restore`); toast.success("Project restored"); load(); onChanged(); }
    catch { toast.error("Restore failed"); }
  };

  const permanent = async (id) => {
    if (confirmId !== id) { setConfirmId(id); setTimeout(() => setConfirmId(null), 3000); return; }
    try { await api.delete(`/admin/projects/${id}/permanent`); toast.success("Permanently deleted"); setConfirmId(null); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="trash-panel">
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><Trash2 size={17} className="text-rose-400" /> {t.nav_trash}</h3>
      <div className="mt-5 space-y-3">
        {items.length === 0 && <p className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-400" data-testid="trash-empty">{t.trash_empty}</p>}
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4" data-testid={`trash-item-${p.id}`}>
            <img src={resolveImage(p.image_url)} alt={p.title} className="h-14 w-20 rounded-md object-cover opacity-60" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-slate-700">{p.title}</p>
              <p className="font-mono text-[10px] text-slate-400">{p.department} · {t.tab_all === "전체" ? "삭제됨" : "deleted"} {(p.deleted_at || "").slice(0, 10)}</p>
            </div>
            <button onClick={() => restore(p.id)} data-testid={`trash-restore-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-emerald-300 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-600 transition-colors hover:bg-emerald-600 hover:text-white">
              <RotateCcw size={12} /> {t.act_restore}
            </button>
            <button onClick={() => permanent(p.id)} data-testid={`trash-permanent-${p.id}`} className={`flex items-center gap-1.5 rounded-md border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${confirmId === p.id ? "border-rose-500 bg-rose-500 text-white" : "border-rose-300 text-rose-500 hover:bg-rose-500 hover:text-white"}`}>
              <Trash2 size={12} /> {confirmId === p.id ? t.act_sure : t.act_delete_forever}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, checking, logout } = useAuth();
  const { t } = useLang();
  const [section, setSection] = useState("overview");
  const [tab, setTab] = useState("pending");
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trashCount, setTrashCount] = useState(0);
  const [rejectId, setRejectId] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/projects").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/admin/analytics").then((r) => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/trash").then((r) => setTrashCount(r.data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (checking) return null;
  if (!user) return <LoginForm />;
  if (user.role !== "admin") {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-5 pt-24 text-center" data-testid="admin-forbidden">
        <p className="font-display text-xl font-semibold text-slate-900">Admin access only</p>
        <p className="max-w-sm text-sm text-slate-500">This console is restricted to faculty administrators. Student accounts can submit projects from the homepage.</p>
        <button onClick={logout} data-testid="admin-forbidden-logout" className="rounded-full border border-slate-200 bg-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 shadow-sm hover:text-rose-500">
          {t.admin_signout}
        </button>
      </div>
    );
  }

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch { toast.error("Action failed"); }
  };

  const filtered = tab === "all" ? projects : projects.filter((p) => p.status === tab);
  const counts = {
    pending: projects.filter((p) => p.status === "pending").length,
    approved: projects.filter((p) => p.status === "approved").length,
    rejected: projects.filter((p) => p.status === "rejected").length,
  };

  const NAV = [
    ["overview", t.nav_overview, LayoutDashboard, null],
    ["projects", t.nav_projects_adm, FolderKanban, counts.pending || null],
    ["trash", t.nav_trash, Trash2, trashCount || null],
    ["news", t.nav_news_adm, Newspaper, null],
    ["features", t.nav_features, SlidersHorizontal, null],
    ["creds", t.nav_creds, KeyRound, null],
  ];

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 lg:px-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#2563EB]">{t.admin_tag}</p>
          <h1 className="mt-2 font-display text-3xl font-bold text-slate-900">{t.admin_title}</h1>
        </div>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 shadow-sm transition-colors hover:border-rose-300 hover:text-rose-500">
          <LogOut size={13} /> {t.admin_signout}
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <aside className="flex gap-2 overflow-x-auto pb-1 lg:flex-col lg:overflow-visible" data-testid="admin-nav">
          {NAV.map(([key, label, Icon, badge]) => (
            <button
              key={key}
              onClick={() => setSection(key)}
              data-testid={`admin-nav-${key}`}
              className={`flex shrink-0 items-center gap-2.5 rounded-lg px-4 py-3 font-mono text-[11px] uppercase tracking-[0.15em] transition-all duration-300 lg:w-full ${
                section === key
                  ? "bg-[#1E3A8A] text-white shadow-[0_8px_22px_rgba(30,58,138,0.3)]"
                  : "border border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"
              }`}
            >
              <Icon size={14} />
              <span className="flex-1 text-left">{label}</span>
              {badge !== null && (
                <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${section === key ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"}`}>{badge}</span>
              )}
            </button>
          ))}
        </aside>

        <div className="min-w-0">
          {section === "overview" && analytics && (
            <div data-testid="admin-overview">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
                {[
                  { label: t.stat_views, value: analytics.totals.views },
                  { label: t.stat_likes, value: analytics.totals.likes },
                  { label: t.stat_approved, value: counts.approved },
                  { label: t.stat_pending, value: counts.pending },
                ].map((s) => (
                  <div key={s.label} className="rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)]">
                    <div className="font-display text-3xl font-bold tabular-nums text-slate-900">{s.value.toLocaleString()}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-6 rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="admin-chart">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t.chart_title}</h3>
                <div className="mt-6 h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analytics.by_department} margin={{ left: -10, right: 10 }}>
                      <XAxis dataKey="department" tick={{ fill: "#64748B", fontSize: 10 }} interval={0} angle={-12} textAnchor="end" height={60} />
                      <YAxis tick={{ fill: "#64748B", fontSize: 11 }} />
                      <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12, boxShadow: "0 8px 24px rgba(15,23,42,0.08)" }} labelStyle={{ color: "#0F172A" }} />
                      <Bar dataKey="views" radius={[4, 4, 0, 0]}>
                        {analytics.by_department.map((_, i) => (
                          <Cell key={i} fill={["#1E3A8A", "#2563EB", "#10B981", "#D97706", "#F43F5E", "#0891B2", "#7C3AED"][i % 7]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="mt-6 rounded-xl border border-slate-200/90 bg-white p-6 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:p-7" data-testid="admin-top-projects">
                <h3 className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">{t.top_title}</h3>
                <div className="mt-4 space-y-1">
                  {analytics.top_projects.map((p, i) => (
                    <div key={p.id} className="flex items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-slate-50">
                      <span className="font-display text-sm font-bold text-slate-300">{i + 1}</span>
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{p.title}</span>
                      <span className="flex items-center gap-1 font-mono text-[10px] text-slate-400"><Eye size={11} /> {p.views.toLocaleString()}</span>
                      <span className="flex items-center gap-1 font-mono text-[10px] text-slate-400"><Heart size={11} /> {p.likes.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {section === "projects" && (
            <div data-testid="admin-projects-section">
              <div className="flex flex-wrap gap-2.5" data-testid="admin-tabs">
                {[["pending", `${t.tab_pending} (${counts.pending})`], ["approved", `${t.tab_approved} (${counts.approved})`], ["rejected", `${t.tab_rejected} (${counts.rejected})`], ["all", `${t.tab_all} (${projects.length})`]].map(([key, label]) => (
                  <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`} className={`rounded-full border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-all duration-300 ${tab === key ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-slate-200 bg-white text-slate-500 hover:border-[#2563EB]/50 hover:text-[#1E3A8A]"}`}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-6 space-y-4" data-testid="admin-project-list">
                {filtered.length === 0 && (
                  <p className="rounded-xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-400" data-testid="admin-empty-list">{t.queue_empty}</p>
                )}
                {filtered.map((p) => (
                  <motion.div key={p.id} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid={`admin-project-${p.id}`} className="flex flex-col gap-5 rounded-xl border border-slate-200/90 bg-white p-5 shadow-[0_4px_20px_-4px_rgba(15,23,42,0.05)] sm:flex-row">
                    <img src={resolveImage(p.image_url)} alt={p.title} className="h-28 w-full rounded-lg object-cover sm:w-44" />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] ${STATUS_STYLE[p.status]}`}>{p.status}</span>
                        {p.featured && (
                          <span className="rounded-full border border-[#2563EB]/40 bg-[#2563EB]/5 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-[#1E3A8A]">{t.act_featured}</span>
                        )}
                        {p.pending_edit && (
                          <span className="rounded-full border border-amber-300 bg-amber-50 px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.18em] text-amber-600" data-testid={`edits-pending-badge-${p.id}`}>{t.adm_edits_pending}</span>
                        )}
                        <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-slate-400">{p.department}</span>
                      </div>
                      <h3 className="mt-2 font-display text-base font-semibold text-slate-900">{p.title}</h3>
                      <p className="mt-1 text-xs leading-relaxed text-slate-500 line-clamp-2">{p.description}</p>
                      <div className="mt-2 flex items-center gap-4 font-mono text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><Eye size={11} /> {p.views}</span>
                        <span className="flex items-center gap-1"><Heart size={11} /> {p.likes}</span>
                      </div>
                      {p.status === "rejected" && p.feedback && (
                        <p className="mt-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-600">Feedback: {p.feedback}</p>
                      )}
                      {rejectId === p.id && (
                        <div className="mt-3 flex gap-2">
                          <input value={feedback} onChange={(e) => setFeedback(e.target.value)} placeholder={t.feedback_ph} data-testid={`reject-feedback-input-${p.id}`} className="flex-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 outline-none focus:border-rose-300" />
                          <button onClick={() => act(() => api.post(`/admin/projects/${p.id}/reject`, { feedback }), "Project rejected")} data-testid={`reject-confirm-${p.id}`} className="rounded-md bg-rose-500 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-white hover:bg-rose-600">
                            {t.act_confirm}
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="flex shrink-0 flex-row flex-wrap gap-2 sm:flex-col">
                      <button onClick={() => setDetail(p)} data-testid={`details-button-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-600 transition-colors hover:border-[#2563EB]/50 hover:text-[#1E3A8A]">
                        <Eye size={12} /> {t.mkt_details}
                      </button>
                      {p.status !== "approved" && (
                        <button onClick={() => act(() => api.post(`/admin/projects/${p.id}/approve`), "Project approved — now live")} data-testid={`approve-button-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-emerald-300 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-emerald-600 transition-colors hover:bg-emerald-600 hover:text-white">
                          <Check size={12} /> {t.act_approve}
                        </button>
                      )}
                      {p.status !== "rejected" && (
                        <button onClick={() => { setRejectId(rejectId === p.id ? null : p.id); setFeedback(""); }} data-testid={`reject-button-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-rose-300 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-rose-500 transition-colors hover:bg-rose-500 hover:text-white">
                          <X size={12} /> {t.act_reject}
                        </button>
                      )}
                      {p.status === "approved" && (
                        <button onClick={() => act(() => api.post(`/admin/projects/${p.id}/feature`), p.featured ? "Removed from featured" : "Featured on homepage")} data-testid={`feature-button-${p.id}`} className={`flex items-center gap-1.5 rounded-md border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] transition-colors ${p.featured ? "border-[#1E3A8A] bg-[#1E3A8A] text-white" : "border-[#2563EB]/40 text-[#1E3A8A] hover:bg-[#2563EB] hover:text-white"}`}>
                          <Star size={12} fill={p.featured ? "currentColor" : "none"} /> {p.featured ? t.act_featured : t.act_feature}
                        </button>
                      )}
                      <button onClick={() => act(() => api.delete(`/admin/projects/${p.id}`), "Moved to trash — recoverable from the Trash tab")} data-testid={`delete-button-${p.id}`} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400 transition-colors hover:border-rose-300 hover:text-rose-500">
                        <Trash2 size={12} /> {t.act_delete}
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {section === "trash" && <TrashPanel t={t} onChanged={load} />}
          {section === "news" && <NewsManager t={t} />}
          {section === "features" && <FeatureToggles t={t} />}
          {section === "creds" && <CredentialsPanel t={t} />}
        </div>
      </div>

      <AnimatePresence>
        {detail && (
          <AdminProjectModal
            project={detail}
            onClose={() => setDetail(null)}
            onSaved={(updated) => { setDetail(updated); load(); }}
          />
        )}
      </AnimatePresence>
    </main>
  );
}
